import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Check, ArrowRight } from "lucide-react";
import { payForPackage } from "@/lib/razorpay";

const accent = { health: "olive", beauty: "rose", education: "navy", combo: "gold" };

export default function Packages() {
  const [pkgs, setPkgs] = useState([]);
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(null);
  const [live, setLive] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/packages").then((r) => setPkgs(r.data));
    api.get("/payments/config").then((r) => setLive(r.data.live)).catch(() => {});
  }, []);

  const activate = (code) => {
    if (!user) { toast.error("Please login first"); nav("/login"); return; }
    if (user.active) { toast.error("You already have an active package"); return; }
    setBusy(code);
    payForPackage({
      packageCode: code,
      user,
      onSuccess: async (data) => {
        toast.success(`Package activated! Order ${data.order_no}${data.mock ? " (test mode)" : ""}`);
        await refresh();
        setBusy(null);
        nav("/dashboard");
      },
      onError: (e) => {
        toast.error(e?.response?.data?.detail || e?.message || "Payment failed");
        setBusy(null);
      },
    });
  };

  return (
    <div className="min-h-screen bg-sand">
      <Navbar />
      <section className="bg-navy text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.4em] text-gold mb-4">Starter Packages</div>
          <h1 className="font-serif text-5xl lg:text-6xl">Pick a package. Become a distributor.</h1>
          <p className="mt-6 max-w-2xl text-white/70 leading-relaxed">
            Every Samara package unlocks your SP, IBA pricing on our full product catalogue, and access to the full binary income plan. Activate any package by referring just 2 IDs of 50 SP in 15 days — startup bonus on us.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pkgs.map((p) => {
            const c = accent[p.category] || "navy";
            return (
              <div key={p.code} data-testid={`pkg-${p.code}`} className="bg-white border border-border hover:border-gold transition-all hover:shadow-lg p-6 flex flex-col rounded-sm">
                <div className={`text-[10px] uppercase tracking-[0.3em] text-${c === "olive" ? "olive" : c === "rose" ? "rose" : c === "gold" ? "gold-deep" : "navy"} mb-3`}>{p.category}</div>
                <h3 className="font-serif text-2xl text-navy leading-tight">{p.name}</h3>
                <p className="text-xs text-navy/50 mt-2">{p.description}</p>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="text-4xl text-navy font-extrabold tabular-nums lining-nums">{inr(p.price)}</span>
                  <span className="text-xs text-navy/40 line-through tabular-nums lining-nums">{inr(p.price * 1.15)}</span>
                </div>
                <div className="mt-2 inline-flex w-fit bg-navy text-gold text-xs font-bold px-2.5 py-1">{p.sp} SP</div>
                <ul className="mt-6 space-y-2 text-sm text-navy/70 flex-1">
                  {(p.benefits || []).map((b, i) => (
                    <li key={`${p.code}-benefit-${i}`} className="flex gap-2"><Check size={14} className="text-gold mt-1 shrink-0" /> {b}</li>
                  ))}
                </ul>
                <button
                  data-testid={`activate-${p.code}`}
                  onClick={() => activate(p.code)}
                  disabled={busy === p.code || !live}
                  title={!live ? "Razorpay integration in progress — activation will open once payment gateway is live." : ""}
                  className="mt-6 w-full bg-gold text-navy py-3 hover:bg-gold-deep transition-colors rounded-sm font-medium inline-flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy === p.code ? "Activating..." : !live ? "Coming Soon" : <>Activate <ArrowRight size={14} /></>}
                </button>
              </div>
            );
          })}
        </div>
        <p className={`text-xs mt-8 text-center ${live ? "text-navy/50" : "text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 max-w-xl mx-auto"}`}>
          {live
            ? "💳 Razorpay is LIVE. Real payment will be processed."
            : "🚧 Payment gateway integration is in progress. Package activation is temporarily disabled — please check back shortly."}
        </p>
      </section>
      <Footer />
    </div>
  );
}
